import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Check, X, ArrowLeft, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = email, 2 = otp + new password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const requestReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      toast.success('If an account exists with this email, a reset code has been sent — check your inbox and spam folder.');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not start reset. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (otp.length !== 6) { setError('Enter the 6-digit code from your email'); return; }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        email,
        otp,
        new_password: newPassword,
      });
      toast.success('Password reset! Log in with your new password.');
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. Check your code and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ background: 'linear-gradient(135deg, rgba(244, 63, 94, 0.08) 0%, rgba(251, 146, 60, 0.08) 100%)' }} data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#0EA5E9] transition-colors mb-6" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>

        <div className="glass-card p-8 shadow-xl">
          <div className="text-center mb-7">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-500 to-orange-500 shadow-md mb-3">
              <ShieldCheck className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-black text-[#111827] mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {step === 1 ? 'Reset your password' : 'Enter your reset code'}
            </h1>
            <p className="text-sm text-[#6B7280]">
              {step === 1
                ? 'Enter your registered email — we\'ll send a 6-digit reset code.'
                : `Code sent to ${email}. Codes expire in 10 minutes.`}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="forgot-error">
              {error}
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={requestReset} className="space-y-4" data-testid="forgot-step1-form">
              <div>
                <label className="block text-sm font-bold text-[#111827] mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-5 h-5 text-[#6B7280]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="w-full pl-10 pr-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#F43F5E] focus:outline-none transition-colors"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(180,83,9)] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
                data-testid="forgot-submit-btn"
              >
                {loading ? 'Sending code…' : 'Send reset code'}
              </button>
              <p className="text-xs text-center text-[#9CA3AF]">
                Need an account? <Link to="/register" className="text-[#0EA5E9] font-bold hover:underline">Sign up</Link>
              </p>
            </form>
          ) : (
            <form onSubmit={doReset} className="space-y-4" data-testid="forgot-step2-form">
              <div>
                <label className="block text-sm font-bold text-[#111827] mb-2">6-digit code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="------"
                  required
                  className="w-full px-4 py-3 text-center text-2xl font-black tracking-[0.5em] border-2 border-[#E5E7EB] rounded-xl focus:border-[#F43F5E] focus:outline-none transition-colors font-mono"
                  style={{ fontFamily: 'Courier New, monospace' }}
                  data-testid="reset-otp-input"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-[#111827] mb-2">New password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-5 h-5 text-[#6B7280]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min. 6 characters"
                    required
                    minLength={6}
                    className="w-full pl-10 pr-12 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#F43F5E] focus:outline-none transition-colors"
                    data-testid="new-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-[#6B7280] hover:text-[#F43F5E]" aria-label="Show/Hide password">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-[#111827] mb-2">Confirm new password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-5 h-5 text-[#6B7280]" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-type new password"
                    required
                    minLength={6}
                    className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-colors ${
                      confirmPassword && newPassword !== confirmPassword ? 'border-rose-300 focus:border-rose-500' : 'border-[#E5E7EB] focus:border-[#F43F5E]'
                    }`}
                    data-testid="confirm-new-password-input"
                  />
                </div>
                {confirmPassword && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-xs">
                    {newPassword === confirmPassword
                      ? <><Check className="w-3.5 h-3.5 text-emerald-500" /><span className="text-emerald-600">Passwords match</span></>
                      : <><X className="w-3.5 h-3.5 text-rose-500" /><span className="text-rose-600">Passwords don't match</span></>}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(180,83,9)] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
                data-testid="reset-submit-btn"
              >
                {loading ? 'Resetting…' : 'Reset password'}
              </button>

              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-xs text-center text-[#6B7280] hover:text-[#F43F5E] transition-colors"
                data-testid="back-to-email"
              >
                Didn't get the code? Use a different email
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
